import { createEffect, onCleanup } from "solid-js";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import * as d3 from "d3";
import { filtered, color, tabledata, tooltip, setTooltip, eventType } from "../../store/globalStore";
import { isDark } from "../../store/themeStore";
import { EMPTY_GROUP_COLOR, getColorByIndex, isEmptyGroupValue } from "../../utils/colorScale";
import { buildColorGrouping, getGroupKeyFromItem } from "../../utils/colorGrouping";
import {
  type StripPoint,
  type WhiskerMode,
  computeTukeyBoxStats,
  getNumericTwsFromRow,
  sortStripPointsByTws,
  stripHorizontalFraction,
} from "../../utils/boxPlotStripOrder";
import { resolveManeuverChannelAlias } from "../../utils/maneuverChannelAliases";
import { getManeuverChannelDisplayLabel } from "../../utils/maneuverChannelLabels";

interface ManeuverBoxPlotProps {
  channels: string[];
  /** Default Tukey whiskers; use `full` for min–max whiskers only. */
  whiskerMode?: WhiskerMode;
}

const BOXPLOT_OVERLAY_OPACITY = 0.7;
const BOXPLOT_STRIP_OPACITY = 0.6;
const BOXPLOT_WHISKER_CAP_HALF = 5;

interface ManeuverGroup {
  key: unknown;
  vals: number[];
  stripOrder: StripPoint[];
  color: string;
  meanVmg: number | null;
}

export default function ManeuverBoxPlot(props: ManeuverBoxPlotProps) {
  let containerRef: HTMLElement | null = null;

  // Calculate VMG intervals (5 intervals from min to max)
  const calculateVmgIntervals = (
    rows: any[]
  ): { min: number; max: number; intervalSize: number; getInterval: (vmg: number) => string | null } | null => {
    const vmgValues = rows
      .map((r) => parseFloat(r.vmg_perc_avg))
      .filter((v) => !isNaN(v) && v !== null && v !== undefined);

    if (vmgValues.length === 0) return null;

    const min = Math.min(...vmgValues);
    const max = Math.max(...vmgValues);

    if (min === max) {
      return {
        min,
        max,
        intervalSize: 0,
        getInterval: () => `${min.toFixed(1)}-${max.toFixed(1)}`,
      };
    }

    const intervalSize = (max - min) / 5;

    return {
      min,
      max,
      intervalSize,
      getInterval: (vmg) => {
        if (vmg < min || vmg > max) return null;
        const intervalIndex = Math.min(4, Math.floor((vmg - min) / intervalSize));
        const intervalMin = min + intervalIndex * intervalSize;
        const intervalMax = intervalIndex === 4 ? max : min + (intervalIndex + 1) * intervalSize;
        return `${intervalMin.toFixed(1)}-${intervalMax.toFixed(1)}`;
      },
    };
  };

  function getChannelValue(row: Record<string, unknown>, channel: string): unknown {
    const chLower = channel.toLowerCase();
    let val = row[channel] ?? row[chLower] ?? row[channel.toUpperCase()];
    if (val !== undefined && val !== null) return val;
    val = resolveManeuverChannelAlias(row, channel);
    if (val !== undefined && val !== null) return val;
    const key = Object.keys(row).find((k) => k.toLowerCase() === chLower);
    return key != null ? row[key] : undefined;
  }

  function buildGroupedValues(channel: string): ManeuverGroup[] {
    const colorMode = String(color() || "TWS").toUpperCase();
    const rows = tabledata().filter((r) => filtered().includes(r.event_id));
    const { scale } = buildColorGrouping(rows, colorMode);

    let vmgIntervals = null;
    if (colorMode === "VMG") {
      vmgIntervals = calculateVmgIntervals(rows);
    }

    const by = new Map<unknown, StripPoint[]>();
    const vmgByGroup = new Map<unknown, number[]>();

    rows.forEach((r) => {
      let k: unknown;
      if (colorMode === "VMG" && vmgIntervals) {
        const vmg = parseFloat(String((r as { vmg_perc_avg?: unknown }).vmg_perc_avg));
        k = vmgIntervals.getInterval(vmg);
        if (!k) return;
      } else {
        k = getGroupKeyFromItem(r as Record<string, unknown>, colorMode);
      }
      if (!by.has(k)) by.set(k, []);
      const v = Number(getChannelValue(r as Record<string, unknown>, channel));
      if (!isNaN(v)) {
        const twsNum = getNumericTwsFromRow(r as Record<string, unknown>, { preferredTwsField: "tws_avg" });
        by.get(k)!.push({ value: v, tws: twsNum ?? Number.NaN });
      }

      if (!vmgByGroup.has(k)) vmgByGroup.set(k, []);
      const vmgVal = parseFloat(r.vmg_perc_avg);
      if (!isNaN(vmgVal) && vmgVal !== null && vmgVal !== undefined) {
        vmgByGroup.get(k)!.push(vmgVal);
      }
    });

    const groups: ManeuverGroup[] = Array.from(by.entries()).map(([k, points], idx) => {
      let c = getColorByIndex(idx);
      try {
        if (colorMode === "TACK") {
          const t = String(k);
          c =
            t === "PORT" || t === "P - S"
              ? "#d62728"
              : t === "STBD" || t === "S - P"
                ? "#2ca02c"
                : c;
        } else if (colorMode === "VMG" && vmgIntervals) {
          const intervalMin = parseFloat(String(k).split("-")[0]);
          if (!isNaN(intervalMin)) {
            c = scale && typeof scale === "function" ? scale(intervalMin) : c;
          }
        } else {
          let valForScale: string | number =
            typeof k === "number" ? k : typeof k === "string" ? k : String(k);
          if (colorMode === "TWS") {
            valForScale = Number(k);
          } else if (colorMode === "SOURCE") {
            valForScale = typeof k === "string" ? k.toLowerCase() : valForScale;
          }
          c =
            scale && typeof scale === "function"
              ? (scale as (v: string | number) => string)(valForScale)
              : c;
        }
      } catch {
        /* keep default color */
      }

      if (isEmptyGroupValue(k)) {
        c = EMPTY_GROUP_COLOR;
      }

      let meanVmg: number | null = null;
      const vmgVals = vmgByGroup.get(k);
      if (vmgVals && vmgVals.length > 0) {
        meanVmg = d3.mean(vmgVals) ?? null;
      }

      const stripOrder = sortStripPointsByTws(points);
      const vals = stripOrder.map((p) => p.value).sort((a, b) => a - b);
      return { key: k, vals, stripOrder, color: c, meanVmg };
    });

    groups.sort((a, b) => {
      if (colorMode === "VMG" && vmgIntervals) {
        const aMin = parseFloat(String(a.key).split("-")[0]);
        const bMin = parseFloat(String(b.key).split("-")[0]);
        if (!isNaN(aMin) && !isNaN(bMin)) {
          return aMin - bMin;
        }
      }
      const ak = String(a.key);
      const bk = String(b.key);
      return ak > bk ? 1 : ak < bk ? -1 : 0;
    });
    return groups;
  }

  function draw() {
    if (!containerRef) return;
    const width = containerRef.clientWidth || 1200;

    d3.select(containerRef).selectAll("svg").remove();

    const channels = props.channels;
    const whiskerMode: WhiskerMode = props.whiskerMode ?? "tukey";
    const margin = { top: 20, right: 60, bottom: 60, left: 60 };
    const cols = 3;
    const colGap = 50;
    const rowGap = 80;
    const plotW = Math.max(260, (width - margin.left - margin.right - (cols - 1) * colGap) / cols);
    const widthScale = 0.85;
    const plotInnerW = Math.max(200, plotW * widthScale);
    const plotH = 400;

    const svg = d3
      .select(containerRef)
      .append("svg")
      .attr("width", width)
      .attr("height", margin.top + margin.bottom + Math.ceil(channels.length / cols) * (plotH + rowGap));

    const textColor = isDark() ? "#ffffff" : "#000000";

    channels.forEach((channel, idx) => {
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      const gx = margin.left + col * (plotW + colGap) + (plotW - plotInnerW) / 2 - 50;
      const gy = margin.top + row * (plotH + rowGap);

      // Sidebar chart-search anchor; an SVG <g> scrolls into view but does not flash.
      const g = svg
        .append("g")
        .attr("transform", `translate(${gx},${gy})`)
        .attr("data-chart-search-title", getManeuverChannelDisplayLabel(channel, eventType()));

      const groups = buildGroupedValues(channel);
      const x = d3.scaleBand().domain(groups.map((d) => String(d.key))).range([0, plotInnerW]).padding(0.3);

      const allBoxStats = groups.map((gp) => computeTukeyBoxStats(gp.vals, whiskerMode));
      const domainVals = allBoxStats.flatMap((s) => [s.whiskerMin, s.whiskerMax, ...s.outliers]).filter((v) => !Number.isNaN(v));
      const rawMin = domainVals.length ? (d3.min(domainVals) ?? 0) : 0;
      const rawMax = domainVals.length ? (d3.max(domainVals) ?? 1) : 1;
      const pad = (rawMax - rawMin) * 0.1 || 0.1;
      const y = d3.scaleLinear().domain([rawMin - pad, rawMax + pad]).nice().range([plotH, 0]);

      const allVals = groups.flatMap((gp) => gp.vals);

      g.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0,${plotH})`)
        .call(d3.axisBottom(x))
        .selectAll("text")
        .attr("transform", "rotate(-30)")
        .style("text-anchor", "end");
      g.append("g").attr("class", "y-axis").call(d3.axisLeft(y).ticks(4));

      g.selectAll(".x-axis text, .y-axis text").style("fill", textColor);

      const overallMean = d3.mean(allVals) ?? 0;
      g.append("line")
        .attr("class", "subaxis")
        .attr("x1", 0)
        .attr("x2", plotInnerW)
        .attr("y1", y(overallMean))
        .attr("y2", y(overallMean))
        .attr("stroke", "#808080")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,5")
        .style("pointer-events", "none");

      g.append("text")
        .attr("x", plotInnerW + 4)
        .attr("y", y(overallMean))
        .attr("text-anchor", "start")
        .attr("font-size", "10px")
        .attr("fill", "#808080")
        .attr("dy", "0.35em")
        .style("pointer-events", "none")
        .text(overallMean.toFixed(1));

      const getTooltipPosition = (event: MouseEvent) => {
        try {
          if (!containerRef) return { x: 0, y: 0 };
          const rect = containerRef.getBoundingClientRect();
          const containerX = event.clientX - rect.left;
          const containerY = event.clientY - rect.top;
          const pad = 12;
          const estWidth = 220;
          const estHeight = 140;

          let xPos = containerX + pad;
          if (containerX + pad + estWidth > rect.width) {
            xPos = Math.max(0, containerX - estWidth - pad);
          }

          let yPos = containerY - 40;
          if (containerY - estHeight < 0) {
            yPos = Math.max(0, containerY + pad);
          } else if (containerY + pad + estHeight > rect.height) {
            yPos = Math.max(0, rect.height - estHeight - pad);
          }
          return { x: xPos, y: yPos };
        } catch {
          return { x: 0, y: 0 };
        }
      };

      type Tip = {
        group: string;
        min: number;
        q1: number;
        q2: number;
        q3: number;
        max: number;
        mean: number;
        count: number;
        outlierCount?: number;
        iqr?: number;
      };

      const showTooltip = (event: MouseEvent, data: Tip) => {
        const pos = getTooltipPosition(event);
        const w = "min-width:50px;";
        let rows = `
              <tr><td>GROUP:</td><td style='${w}'>${data.group}</td></tr>
              <tr><td>COUNT:</td><td style='${w}'>${data.count}</td></tr>`;
        if (whiskerMode === "tukey") {
          rows += `<tr><td>WHISKERS:</td><td style='${w}'>Tukey (1.5×IQR), not full range</td></tr>`;
        }
        if (whiskerMode === "tukey" && data.outlierCount !== undefined && data.outlierCount > 0) {
          rows += `<tr><td>OUTLIERS:</td><td style='${w}'>${data.outlierCount}</td></tr>`;
          if (data.iqr !== undefined) rows += `<tr><td>IQR:</td><td style='${w}'>${data.iqr.toFixed(2)}</td></tr>`;
        }
        rows += `
              <tr><td>MAX:</td><td style='${w}'>${data.max.toFixed(2)}</td></tr>
              <tr><td>Q3:</td><td style='${w}'>${data.q3.toFixed(2)}</td></tr>
              <tr><td>MEAN:</td><td style='${w}'>${data.mean.toFixed(2)}</td></tr>
              <tr><td>MEDIAN:</td><td style='${w}'>${data.q2.toFixed(2)}</td></tr>
              <tr><td>Q1:</td><td style='${w}'>${data.q1.toFixed(2)}</td></tr>
              <tr><td>MIN:</td><td style='${w}'>${data.min.toFixed(2)}</td></tr>`;
        const content = `<table class='table-striped'>${rows}</table>`;
        setTooltip({ visible: true, content, x: pos.x, y: pos.y });
      };

      const hideTooltip = () => {
        setTooltip({ visible: false, content: "", x: 0, y: 0 });
      };

      let highestVmgGroup: { key: unknown; meanVmg: number | null } | null = null;
      const isPerformanceData = groups.some((g) => g.meanVmg !== null && g.meanVmg !== undefined);
      if (isPerformanceData) {
        highestVmgGroup = groups.reduce(
          (max, gp) => {
            if (gp.meanVmg !== null && gp.meanVmg !== undefined) {
              if (max === null || gp.meanVmg > max.meanVmg!) {
                return { key: gp.key, meanVmg: gp.meanVmg };
              }
            }
            return max;
          },
          null as { key: unknown; meanVmg: number | null } | null
        );
      }

      const boxWidth = Math.max(10, x.bandwidth() * 0.6);
      groups.forEach((gp, gi) => {
        const stats = allBoxStats[gi]!;
        const { q1, q2, q3, mean, whiskerMin, whiskerMax, outliers, iqr } = stats;
        const dataMin = gp.vals.length ? gp.vals[0]! : 0;
        const dataMax = gp.vals.length ? gp.vals[gp.vals.length - 1]! : 0;
        const xCenter = (x(String(gp.key)) ?? 0) + x.bandwidth() / 2;
        const boxY = y(q3);
        const boxHeight = Math.max(1, y(q1) - y(q3));

        const tip: Tip = {
          group: String(gp.key),
          min: dataMin,
          q1,
          q2,
          q3,
          max: dataMax,
          mean,
          count: gp.vals.length,
        };
        if (whiskerMode === "tukey") {
          tip.outlierCount = outliers.length;
          tip.iqr = iqr;
        }

        const boxGroup = g.append("g").attr("class", "box-group").style("cursor", "pointer");

        boxGroup
          .append("rect")
          .attr("x", x(String(gp.key)) ?? 0)
          .attr("y", 0)
          .attr("width", x.bandwidth())
          .attr("height", plotH)
          .attr("fill", "transparent")
          .style("pointer-events", "all");

        const stripOrder = gp.stripOrder;
        const nStrip = stripOrder.length;
        if (nStrip > 0) {
          const pointR = Math.max(1.2, Math.min(3.5, boxWidth * 0.075));
          const innerLeft = xCenter - boxWidth / 2 + pointR;
          const innerSpan = Math.max(0, boxWidth - 2 * pointR);
          stripOrder.forEach((pt, j) => {
            const fx = stripHorizontalFraction(j, nStrip);
            const cx = innerLeft + fx * innerSpan;
            boxGroup
              .append("circle")
              .attr("cx", cx)
              .attr("cy", y(pt.value))
              .attr("r", pointR)
              .attr("fill", gp.color)
              .attr("opacity", BOXPLOT_STRIP_OPACITY)
              .attr("stroke", "#222")
              .attr("stroke-width", 0.35)
              .style("pointer-events", "none");
          });
        }

        boxGroup
          .append("line")
          .attr("x1", xCenter)
          .attr("x2", xCenter)
          .attr("y1", y(whiskerMin))
          .attr("y2", y(whiskerMax))
          .attr("stroke", "#555")
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .style("pointer-events", "none");

        boxGroup
          .append("line")
          .attr("x1", xCenter - BOXPLOT_WHISKER_CAP_HALF)
          .attr("x2", xCenter + BOXPLOT_WHISKER_CAP_HALF)
          .attr("y1", y(whiskerMin))
          .attr("y2", y(whiskerMin))
          .attr("stroke", "#555")
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .style("pointer-events", "none");

        boxGroup
          .append("line")
          .attr("x1", xCenter - BOXPLOT_WHISKER_CAP_HALF)
          .attr("x2", xCenter + BOXPLOT_WHISKER_CAP_HALF)
          .attr("y1", y(whiskerMax))
          .attr("y2", y(whiskerMax))
          .attr("stroke", "#555")
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .style("pointer-events", "none");

        if (groups.length > 1 && isPerformanceData && highestVmgGroup && String(gp.key) === String(highestVmgGroup.key)) {
          boxGroup
            .append("text")
            .attr("x", xCenter)
            .attr("y", y(whiskerMax) - 15)
            .attr("text-anchor", "middle")
            .attr("font-size", "20px")
            .attr("fill", "#FFD700")
            .style("pointer-events", "none")
            .text("★");
        }

        boxGroup
          .append("rect")
          .attr("x", xCenter - boxWidth / 2)
          .attr("y", boxY)
          .attr("width", boxWidth)
          .attr("height", boxHeight)
          .attr("fill", gp.color)
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .attr("stroke", "#333")
          .style("pointer-events", "none");

        boxGroup
          .append("line")
          .attr("x1", xCenter - boxWidth / 2)
          .attr("x2", xCenter + boxWidth / 2)
          .attr("y1", y(q2))
          .attr("y2", y(q2))
          .attr("stroke", textColor)
          .attr("stroke-width", 2)
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .style("pointer-events", "none");

        boxGroup
          .append("line")
          .attr("x1", xCenter - boxWidth / 2)
          .attr("x2", xCenter + boxWidth / 2)
          .attr("y1", y(mean))
          .attr("y2", y(mean))
          .attr("stroke", textColor)
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "4,2")
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .style("pointer-events", "none");

        boxGroup
          .append("circle")
          .attr("cx", xCenter)
          .attr("cy", y(mean))
          .attr("r", 4)
          .attr("fill", textColor)
          .attr("stroke", textColor)
          .attr("stroke-width", 1)
          .attr("opacity", BOXPLOT_OVERLAY_OPACITY)
          .style("pointer-events", "none");

        boxGroup
          .on("mouseover", (event) => {
            event.stopPropagation();
            showTooltip(event as MouseEvent, tip);
          })
          .on("mousemove", (event) => {
            event.stopPropagation();
            showTooltip(event as MouseEvent, tip);
          })
          .on("mouseout", (event) => {
            event.stopPropagation();
            hideTooltip();
          });
      });

      if (groups.length < 7) {
        const bw = Math.max(10, x.bandwidth() * 0.6);
        groups.forEach((gp) => {
          const mean = gp.vals.length ? (d3.mean(gp.vals) ?? 0) : 0;
          const xCenter = (x(String(gp.key)) ?? 0) + x.bandwidth() / 2;
          const meanY = y(mean);
          const labelX = xCenter + bw / 2 + 4;
          g.append("text")
            .attr("x", labelX)
            .attr("y", meanY)
            .attr("text-anchor", "start")
            .attr("font-size", "10px")
            .attr("fill", textColor)
            .attr("dy", "0.35em")
            .style("pointer-events", "none")
            .style("font-weight", "normal")
            .style("opacity", 1)
            .text(mean.toFixed(1));
        });
      }

      svg
        .append("text")
        .attr("class", "y-label chart-element y-label--start")
        .attr("transform", `translate(${gx + 25}, ${gy + 10})`)
        .attr("font-size", "16px")
        .attr("fill", textColor)
        .text(getManeuverChannelDisplayLabel(channel, eventType()));
    });
  }

  createEffect(() => {
    filtered();
    color();
    tabledata();
    props.channels;
    props.whiskerMode;
    draw();
  });

  onCleanup(() => {
    d3.select(containerRef).selectAll("*").remove();
    try {
      setTooltip({ visible: false, content: "", x: 0, y: 0 });
    } catch {
      /* noop */
    }
  });

  return (
    <div
      ref={(el) => (containerRef = el)}
      style={{ width: "100%", height: "100%", position: "relative", padding: "10px 30px 20px 50px" }}
    >
      <div
        id="tt"
        class="tooltip"
        style={{
          opacity: tooltip().visible ? 1 : 0,
          left: `${tooltip().x}px`,
          top: `${tooltip().y}px`,
          position: "absolute",
          "pointer-events": "none",
          "z-index": 9999,
        }}
        innerHTML={sanitizeTooltipHtml(tooltip().content)}
      ></div>
    </div>
  );
}
